<template>
  <div
    class="flex flex-col items-center mx-4 mt-3 px-2"
    id="a4-receipt"
    :class="{
      'dir-ltr': $ctx.receiptLanguage == 'en',
      'dir-rtl': $ctx.receiptLanguage == 'ar'
    }"
  >
    <div class="flex flex-col items-center w-full a4-receipt-page" style="height: 100vh;">
      <!-- header info -->
      <div id="a4-receipt-header" class="flex flex-col w-full">
        <div class="flex mt-4 justify-between items-center w-full">
          <div class="mx-8 mt-3 pb-2">
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/map-marker.svg" width="16" height="16" class="shrink-0" />
              <span>{{ settingsStore.getValue('company_address') }}</span>
            </div>
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/phone.svg" width="16" height="16" class="shrink-0" />
              <span dir="ltr">{{ settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2')? ` - ${settingsStore.getValue('company_phone2')}`: '') }}</span>
            </div>
            <div class="flex items-center gap-6">
              <div v-if="settingsStore.getValue('company_email')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/email.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_email') }}</span>
              </div>
              <div v-if="settingsStore.getValue('company_website')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/web.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_website') }}</span>
              </div>
            </div>
          </div>

          <div class="mx-8">
            <img
              :src="settingsStore.getValue('receipt_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('receipt_logo'): '/images/logo.svg'"
              :width="settingsStore.getValue('receipt_logo_width')"
              :style="{'margin-top': settingsStore.getValue('receipt_logo_top_margin') + 'px', 'margin-right': settingsStore.getValue('receipt_logo_right_margin') + 'px'}"
              alt=""
            />
          </div>
        </div>

        <div class="flex flex-col w-full items-center">
          <div class="w-11/12 h-[3px] rounded-full"
              :style="{background: `linear-gradient(to right, transparent, ${settingsStore.getValue('receipt_branding_color') || '#585954'}, transparent)`}"></div>
        </div>
      </div>

      <div id="a4-receipt-body" class="w-full">
        <!-- title -->
        <div class="flex flex-col w-full mt-2 items-center">
          <h2 class="bg-gray-200 rounded py-1 px-16 font-bold mt-3 capitalize">
            <p>{{ receiptTranslate("Inventory Receiving Receipt", "receipt.inventoryReceivingReceipt") }}</p>
          </h2>
        </div>

        <!-- inventory receiving info and barcode -->
        <div class="flex w-full justify-around">
          <div>
            <table class="w-full text-sm">
              <tr>
                <td><p>{{ receiptTranslate("Vendor", "receipt.vendor") }}</p></td>
                <td><p class="font-bold mx-5">{{ inventoryReceiving?.vendor?.name || inventoryReceiving?.vendorName }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.user">
                <td><p>{{ receiptTranslate("Received By", "receipt.receivedBy") }}</p></td>
                <td><p class="font-bold mx-5">{{ inventoryReceiving?.user?.fullName }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.receivedOn">
                <td><p>{{ receiptTranslate("Received On", "receipt.receivedOn") }}</p></td>
                <td><p class="font-bold mx-5" dir="ltr">{{ formatPostgresDate(inventoryReceiving.receivedOn, 'YYYY-MM-DD') }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.location">
                <td><p>{{ receiptTranslate("Location", "receipt.location") }}</p></td>
                <td><p class="font-bold mx-5">{{ $ctx.locale === 'ar' ? inventoryReceiving?.location?.arName : inventoryReceiving?.location?.enName }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.notes">
                <td><p>{{ receiptTranslate("Notes", "receipt.notes") }}</p></td>
                <td><p class="font-bold mx-5">{{ inventoryReceiving?.notes }}</p></td>
              </tr>
            </table>
          </div>

          <div class="flex flex-col items-center">
            <div>
              <svg :id="`barcode-${inventoryReceiving?.externalID}`" class="mt-3"></svg>
              <div class="flex items-center justify-center">
                <span class="font-bold mx-5" style="font-size: calc(20px * var(--receipt-font-scale, 1));">
                  {{ inventoryReceiving?.externalID?.toString().slice(0, 5) }}-{{ inventoryReceiving?.externalID?.toString().slice(5) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- received items table -->
        <div class="w-full px-5 my-1">
          <table class="receipt-table w-full">
            <thead>
              <tr>
                <th>{{ receiptTranslate("Item Name", "receipt.itemName") }}</th>
                <th class="text-center">{{ receiptTranslate("Qty", "receipt.qty") }}</th>
                <th class="text-right">{{ receiptTranslate("Cost", "receipt.cost") }}</th>
                <th class="text-right">{{ receiptTranslate("Total", "receipt.total") }}</th>
              </tr>
            </thead>
            <tbody v-if="inventoryReceiving?.inventoryReceivingItems">
              <tr v-for="(iri, index) in inventoryReceiving?.inventoryReceivingItems" :key="iri.id">
                <td>{{ iri.item?.name || iri.item?.displayName }}</td>
                <td class="text-center">{{ iri.quantity }}</td>
                <td class="text-right">{{ formatDinero(iri.costDinero).replace("IQD", "") }}</td>
                <td class="text-right">{{ formatDinero(iri.costDinero.multiply(iri.quantity)).replace("IQD", "") }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- financial summary -->
        <div class="text-sm flex flex-col w-full items-end mt-4">
          <div class="flex-col w-5/12 mr-10">
            <div class="px-4 w-full">
              <table class="payment-table" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                <tr>
                  <td>{{ receiptTranslate("Total (before extras)", "receipt.totalBeforeExtras") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.totalCostDinero) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Other Costs", "receipt.otherCosts") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.vendorBill?.otherCostsDinero || inventoryReceiving?.totalCostDinero?.multiply(0)) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Total", "receipt.total") }}</td>
                  <td><p class="text-right">{{ formatDinero(grandTotal) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Money Owed", "receipt.moneyOwed") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.vendorBill?.totalDueDinero || grandTotal) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Payments Made", "receipt.paymentsMade") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.vendorBill?.totalPaidDinero || inventoryReceiving?.totalCostDinero?.multiply(0)) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Remaining Balance", "receipt.remainingBalance") }}</td>
                  <td><p class="text-right font-bold">{{ formatDinero(inventoryReceiving?.vendorBill?.totalBalanceDinero || grandTotal) }}</p></td>
                </tr>
              </table>
            </div>
          </div>
        </div>

        <!-- transactions list (payments) -->
        <div v-if="transactions.length > 0" class="text-sm flex flex-col w-full items-end mt-6">
          <div class="flex-col w-5/12 mr-10">
            <div class="px-4 w-full" v-for="transaction in transactions" :key="transaction.id">
              <div class="border-t border-gray-300 pt-2 mt-2">
                <table class="payment-table w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                  <tr>
                    <td colspan="2">
                      <p class="font-bold">
                        {{ receiptTranslate("Payment ID", "receipt.paymentID") }}: {{ transaction.externalID }}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ receiptTranslate("Date", "receipt.date") }}</td>
                    <td><p class="text-right">{{ parseFormat(transaction.createdAt) }}</p></td>
                  </tr>
                  <tr>
                    <td>{{ receiptTranslate("Amount", "receipt.amount") }}</td>
                    <td><p class="text-right">{{ formatDinero(transaction.amountDinero) }}</p></td>
                  </tr>
                  <tr v-if="transaction.description">
                    <td>{{ receiptTranslate("Notes", "receipt.notes") }}</td>
                    <td><p class="text-right">{{ transaction.description }}</p></td>
                  </tr>
                  <tr v-if="transaction.user && settingsStore.getValue('hide_cashier_name_in_receipt') != 'true'">
                    <td>{{ receiptTranslate("Cashier", "receipt.cashier") }}</td>
                    <td><p class="text-right">{{ transaction.user.fullName }}</p></td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="a4-receipt-footer"
        v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
        class="flex justify-center self-center w-full mt-auto"
      >
        <div class="flex items-center justify-between py-4 px-4 mt-2" style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6">
          <p><img src="@/assets/images/thecky-qr.png" style="height: 25px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 25px;"/></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { formatPostgresDate, parseFormat } from "@/utils/date";
import { useI18n } from "vue-i18n";
import { formatDinero } from "@/utils/money";
import { Transaction } from "@/models/accounting/Transaction";
import JsBarcode from "jsbarcode";
import { useSettingsStore } from "@/stores/settingsStore";
import InventoryReceiving from "@/models/inventory/InventoryReceiving";
import { receiptPrintCSS } from "@/utils/receiptPrint";

export default defineComponent({
  components: {},

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          receipt: {
            inventoryReceivingReceipt: 'Inventory Receiving Receipt',
            vendor: 'Vendor',
            receivedBy: 'Received By',
            receivedOn: 'Received On',
            location: 'Location',
            notes: 'Notes',
            receivedItems: 'Received Items',
            number: '#',
            itemName: 'Item Name',
            quantity: 'Quantity',
            qty: 'Qty',
            cost: 'Cost',
            total: 'Total',
            otherCosts: 'Other Costs',
            grandTotal: 'Grand Total',
            totalItemsPurchased: 'Total Items Purchased',
            paymentTransactions: 'Payment Transactions',
            paymentID: 'Payment ID',
            date: 'Date',
            amount: 'Amount',
            cashier: 'Cashier',
            totalBeforeExtras: 'Total (before extras)',
            moneyOwed: 'Money Owed',
            paymentsMade: 'Payments Made',
            remainingBalance: 'Remaining Balance'
          }
        },
        ar: {
          receipt: {
            inventoryReceivingReceipt: 'ايصال استلام مخزون',
            vendor: 'المورد',
            receivedBy: 'استلم من قبل',
            receivedOn: 'تاريخ الاستلام',
            location: 'الموقع',
            notes: 'ملاحظات',
            receivedItems: 'المواد المستلمة',
            number: '#',
            itemName: 'اسم المادة',
            quantity: 'الكمية',
            qty: 'الكمية',
            cost: 'التكلفة',
            total: 'المجموع',
            otherCosts: 'تكاليف اخرى',
            grandTotal: 'المجموع الكلي',
            totalItemsPurchased: 'عدد المواد المشتراة',
            paymentTransactions: 'حركات الدفع',
            paymentID: 'رقم الدفعة',
            date: 'التاريخ',
            amount: 'المبلغ',
            cashier: 'الكاشير',
            totalBeforeExtras: 'المجموع (قبل التكاليف الإضافية)',
            moneyOwed: 'المبلغ المطلوب',
            paymentsMade: 'المبلغ المدفوع',
            remainingBalance: 'المبلغ المتبقي'
          }
        }
      }
    });

    const settingsStore = useSettingsStore();
    return { t, settingsStore, formatPostgresDate };
  },

  props: {
    inventoryReceiving: {
      type: Object as PropType<InventoryReceiving>,
      required: true
    },
    transactions: {
      type: Array as PropType<Array<Transaction>>,
      required: true
    }
  },

  computed: {
    totalItemsCount() {
      if (!this.inventoryReceiving?.inventoryReceivingItems) return 0;
      return this.inventoryReceiving.inventoryReceivingItems.reduce((acc, item) => acc + (item.quantity || 0), 0);
    },
    grandTotal() {
      const total = this.inventoryReceiving?.totalCostDinero;
      const otherCosts = this.inventoryReceiving?.vendorBill?.otherCostsDinero;
      if (total && otherCosts) {
        return total.add(otherCosts);
      }
      return total;
    }
  },

  mounted() {
    setTimeout(() => {
      if (this.inventoryReceiving?.externalID) {
        JsBarcode(
          `#barcode-${this.inventoryReceiving.externalID}`,
          String(this.inventoryReceiving.externalID),
          {
            height: 50,
            displayValue: false
          }
        );
      }
    }, 100);
  },

  created() {
    this.addPrintCSS();
  },

  beforeUnmount() {
    this.removePrintCSS();
  },

  methods: {
    parseFormat,
    formatDinero,
    addPrintCSS() {
      const style = document.createElement("style");
      style.setAttribute("id", "print-css");
      style.textContent = receiptPrintCSS(this.$ctx.getReceiptSize());
      document.head.appendChild(style);
    },
    removePrintCSS() {
      const style = document.querySelector("#print-css");
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == "en" ? value : this.t(translateKey, this.$ctx.receiptLanguage);
    }
  }
});
</script>

<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

#a4-receipt {
  width: 210mm;
  overflow: scroll;
  background-color: #fff;
}

.payment-table {
  width: 100%;
  border-collapse: collapse;
}

.payment-table td {
  padding: 4px;
}

.receipt-table {
  border-collapse: collapse;
  width: 100%;
}

.receipt-table th,
.receipt-table td {
  text-align: left;
  padding: 2px;
}

.receipt-table thead tr {
}

@media print {
  body * {
    page-break-after: avoid;
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #a4-receipt,
  #a4-receipt * {
    visibility: visible;
  }

  #a4-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0cm;
  }

  #a4-receipt-header {
    top: 0;
  }

  #a4-receipt-body {
    position: relative;
  }

  p,
  div {
    page-break-inside: avoid;
  }
}
</style>
